import { Button } from "@mindstonehq/ui";
import { LogoPurple } from "../icons/logo-purple";
import React from "react";
import diverVsAiSvg from "~/assets/diver-vs-ai.svg";
import feedbackWaveSvg from "~/assets/feedback-wave.svg";
import { Trans } from "@lingui/react/macro";
import { useCustomerSupport } from "~/hooks/customer-support";

interface FeedbackProps {
  code: string | number;
  title: string;
  subtitle?: string;
  /**
   * Optional custom call-to-action element(s). When provided the default actions are hidden.
   */
  cta?: React.ReactElement;
  /**
   * Hide the default "Back to home" button.
   */
  hideHome?: boolean;
  /**
   * Custom path for the illustration. Defaults to /diver-vs-ai.svg
   */
  illustrationPath?: string;
  /**
   * Custom path for the wave SVG. Defaults to /feedback-wave.svg
   */
  wavePath?: string;
}

export function Feedback(props: FeedbackProps) {
  const support = useCustomerSupport();
  const {
    code,
    cta,
    title,
    subtitle,
    hideHome,
    illustrationPath = diverVsAiSvg,
    wavePath = feedbackWaveSvg,
  } = props;

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center bg-quaternary px-4 py-16 sm:px-6 sm:py-24 lg:px-8 w-full overflow-hidden">
      {/* Top banner with logo */}
      <div className="absolute left-0 top-0 w-full">
        <div className="mx-auto flex items-center px-6 pt-6 md:px-12">
          <a href="/">
            <LogoPurple className="h-6" />
          </a>
        </div>
      </div>

      <div className="flex w-full max-w-[1280px] flex-col lg:flex-row lg:items-stretch items-center gap-10 lg:gap-20">
        <img
          src={illustrationPath}
          alt="Illustration"
          className="max-w-[320px] lg:max-w-[380px] select-none lg:mx-0"
        />

        <div className="flex flex-col items-center justify-center text-center lg:items-start lg:text-left max-w-prose lg:max-w-none">
          <h3 className="mt-4 text-foreground text-3xl font-bold">{title}</h3>

          {subtitle && (
            <p className="mt-2 text-muted-foreground max-w-prose whitespace-pre-line text-base">
              {subtitle}
            </p>
          )}

          <div className="mt-8 flex flex-col items-center gap-4 md:flex-row md:justify-start">
            {cta ? (
              cta
            ) : (
              <>
                {!hideHome && (
                  <a href="/">
                    <Button variant="secondary" size="lg">
                      <Trans>Back to home</Trans>
                    </Button>
                  </a>
                )}
                {support.available && (
                  <Button variant="outline" size="lg" onClick={support.open}>
                    <Trans>Contact support</Trans>
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Decorative wave */}
      <img
        src={wavePath}
        aria-hidden
        className="pointer-events-none absolute bottom-0 left-0 w-full select-none h-[27vh] object-cover"
      />
    </div>
  );
}
